import React, { Component } from 'react';
import history from '../history';
import Auth from '../auth/auth';
import SessionManager from '../auth/session-manager';
import { loadServerConfig } from '../auth/session-config';
import SessionWarningDialog from './session-warning-dialog';

const auth = new Auth();

// #628: layout-agnostic session monitor. Wrapping every authenticated route (in
// main.js AppRoute/ProtectedFeatureRoute) means /context, /user-manual, /admin,
// /adminprofile — and any future layout — can't silently opt out of the idle
// timeout + warning dialog. The absolute 24h cap is enforced server-side
// (verifytoken.js + refreshToken); these client timers are the UX layer on top.
class SessionGuard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showSessionWarning: false,
      sessionWarningType: 'idle',
      sessionSecondsRemaining: 300,
      isExtendingSession: false,
    };
    this._unmounted = false;
    this.sessionManager = new SessionManager(auth);
  }

  async componentDidMount() {
    // Align client timers with the server policy before starting. Guard the
    // unmount-before-resolve race so a navigated-away guard never starts timers.
    await loadServerConfig();
    if (this._unmounted) return;
    this.sessionManager.startSession(
      this.handleSessionWarning,
      this.handleSessionTimeout,
      this.handleTokenRefreshFailed,
      this.handleBackgroundRefreshFailed,
    );
  }

  componentWillUnmount() {
    this._unmounted = true;
    this.sessionManager.stopSession();
  }

  handleSessionWarning = (warningInfo) => {
    this.setState({
      showSessionWarning: true,
      sessionWarningType: warningInfo.type,
      sessionSecondsRemaining: warningInfo.secondsRemaining,
    });
  }

  handleSessionTimeout = (type) => {
    let message = 'You have been signed out due to inactivity. Please log in again.';
    if (type === 'sliding-window') {
      message = 'You have been signed out for security reasons. Please log in again.';
    } else if (type === 'token-refresh-failed') {
      message = 'Your session could not be refreshed. Please log in again.';
    }
    sessionStorage.setItem('sessionTimeoutMessage', message);
    this.sessionManager.stopSession();
    auth.logout();
    history.push('/login');
  }

  handleTokenRefreshFailed = () => {
    this.handleSessionTimeout('token-refresh-failed');
  }

  // Background refresh failed — prompt instead of redirecting silently (#606).
  handleBackgroundRefreshFailed = () => {
    this.setState({
      showSessionWarning: true,
      sessionWarningType: 'token-refresh-failed',
      sessionSecondsRemaining: 120,
      isExtendingSession: false,
    });
  }

  handleExtendSession = async () => {
    this.setState({ isExtendingSession: true });
    const success = await this.sessionManager.extendSession();
    if (success) {
      this.setState({ showSessionWarning: false, isExtendingSession: false });
    } else {
      this.setState({ isExtendingSession: false });
      this.handleSessionTimeout('token-refresh-failed');
    }
  }

  handleLogoutFromWarning = () => {
    let message = 'You have been signed out due to inactivity. Please log in again.';
    if (this.state.sessionWarningType === 'sliding-window-warning') {
      message = 'You have been signed out for security reasons. Please log in again.';
    } else if (this.state.sessionWarningType === 'token-refresh-failed') {
      message = 'Your session could not be refreshed. Please log in again.';
    }
    sessionStorage.setItem('sessionTimeoutMessage', message);
    this.sessionManager.stopSession();
    auth.logout();
    history.push('/login');
  }

  render() {
    return (
      <React.Fragment>
        {this.props.children}
        <SessionWarningDialog
          open={this.state.showSessionWarning}
          warningType={this.state.sessionWarningType}
          secondsRemaining={this.state.sessionSecondsRemaining}
          onExtend={this.handleExtendSession}
          onLogout={this.handleLogoutFromWarning}
          isExtending={this.state.isExtendingSession}
        />
      </React.Fragment>
    );
  }
}

export default SessionGuard;
